import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import ExpandedFdocCommentsTab from './ExpandedFdocCommentsTab.js';
import { useExpandedFdocContext } from './ExpandedFdocProvider.js';
import { ExpandedFdocElements } from './ExpandedFdocElements.js';
import { useResourceDataContextSafe } from '../context/resourceDataContext.js';
import { useQueryComments } from '../../../comments/queries/useQueryComments.js';
import { ExtraPanelSidebar } from '../ExpandedResource/ExtraPanel/Sidebar.js';
import ExpandedFdocSidebarAssistant from './ExpandedFdoc__Assistant.js';
import { ExtraPanelInfo } from '../ExpandedResource/ExtraPanel/Info/ExtraPanelInfo.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import useDelayedTransition from '@fabric-app/hooks/useDelayedTransition';
import { Tabs } from '@fabric-app/ui/components/Tabs';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { Flex } from '@fabric-app/ui/components/Flex';
import { useExpandedResourceState } from '../../../resources/stores/useExpandedResourceStore.js';
import { ResourceSidebarHeader } from './ResourceSidebarHeader.js';
import { isDefined } from '@fabric-app/utils/guards';
import { useContextShareToken } from '../../../permission/contexts/AnonShareTokenContext.js';
import { IconSidePanelChatBubble, IconSidePanelInfo, IconSidePanelAssistant } from '@fabric-app/icons/svg';

const Badge = styled.span.withConfig({
  displayName: "ExpandedFdocSidebar__Badge",
  componentId: "sc-3e2c720d-0"
})([
  `display:flex;align-items:center;justify-content:center;padding:3px 5px;min-width:25px;height:20px;border-radius:8px;font-family:Inter,sans-serif;font-size:11.5px;font-weight:500;letter-spacing:-0.26px;background:rgba(`,
  `,0.3);color:white;`
], cssVar["color-bg-contrast-reverse-rgb"]);
const ExpandedFdocSidebar = /* @__PURE__ */ forwardRef(({ showCommentsMobile, isFullscreen, splitContentElement, onClose }, ref) => {
  const { resource } = useResourceDataContextSafe();
  const shareToken = useContextShareToken();
  const isPublished = shareToken?.isPublishedView;
  const { comments } = useQueryComments(resource.id, {
    enabled: !resource.isDraft && !isPublished
  });
  const [editingComment, setEditingComment] = useState(null);
  useEffect(() => {
    if (!editingComment || comments.find((c) => c.id === editingComment.id)) return;
    setEditingComment(null);
  }, [
    comments,
    editingComment
  ]);
  const canAutoFocus = useDelayedTransition(false, true, 100);
  const { isDesktopView } = useResponsive();
  const { selectedExtraPanel: selectedSidePanel, setSelectedExtraPanel: setSelectedSidePanel, sidebarOpen, setSidebarOpen } = useExpandedFdocContext();
  const { expandedResourceSidebarOpen: storedExpandedFdocSidebarOpen, setExpandedResourceSidebarOpen: setStoredExpandedFdocSidebarOpen, expandedResourceSidebarInitTab } = useExpandedResourceState();
  const expandedFdocSidebarOpen = useMemo(() => sidebarOpen ?? storedExpandedFdocSidebarOpen, [
    sidebarOpen,
    storedExpandedFdocSidebarOpen
  ]);
  const setExpandedFdocSidebarOpen = useMemo(() => setSidebarOpen ?? setStoredExpandedFdocSidebarOpen, [
    setSidebarOpen,
    setStoredExpandedFdocSidebarOpen
  ]);
  useEffect(() => {
    if (selectedSidePanel) return;
    if (showCommentsMobile || expandedFdocSidebarOpen) setSelectedSidePanel("assistant");
  }, [
    showCommentsMobile,
    setSelectedSidePanel,
    selectedSidePanel,
    expandedFdocSidebarOpen
  ]);
  const onClickToggleSidebarDesktop = (newSelectedSidePanel) => {
    if (!isDesktopView || resource.isDraft) return;
    setSelectedSidePanel(newSelectedSidePanel);
    setExpandedFdocSidebarOpen(!expandedFdocSidebarOpen);
  };
  const onCancelEditingComment = () => {
    setEditingComment(null);
  };
  const animateIn = isDefined(expandedResourceSidebarInitTab) && expandedResourceSidebarInitTab !== selectedSidePanel;
  const element = /* @__PURE__ */ jsx(ForcedTheme, {
    asChild: true,
    colorScheme: "dark",
    disabled: isFullscreen,
    children: /* @__PURE__ */ jsxs(Container, {
      children: [
        /* @__PURE__ */ jsx(ResourceSidebarHeader, {
          isFullscreen,
          handleClose: onClose
        }),
        /* @__PURE__ */ jsx(ExpandedFdocElements.ItemPreviewSidebar, {
          "data-disabled": resource.isDraft,
          "data-desktop-sidebar-closed": !expandedFdocSidebarOpen || resource.isDraft,
          "data-fullscreen": isFullscreen,
          "data-testid": "expanded-fdoc-sidebar",
          ref,
          children: /* @__PURE__ */ jsx(Tabs.Root, {
            value: selectedSidePanel ?? "assistant",
            onValueChange: (value) => setSelectedSidePanel(value),
            asChild: true,
            children: /* @__PURE__ */ jsxs(ExtraPanelSidebar.Content, {
              children: [
                /* @__PURE__ */ jsxs(ExpandedFdocElements.ItemPreviewSidebarHeaderTitleContainer, {
                  children: [
                    isPublished ? null : /* @__PURE__ */ jsx(ExpandedFdocElements.ItemPreviewSidebarHeaderTitle, {
                      onClick: () => onClickToggleSidebarDesktop("comments"),
                      children: /* @__PURE__ */ jsx(IconSidePanelChatBubble, {})
                    }),
                    /* @__PURE__ */ jsx(ExpandedFdocElements.ItemPreviewSidebarHeaderTitle, {
                      "data-similar-button": "true",
                      onClick: () => onClickToggleSidebarDesktop("info"),
                      tabIndex: 7,
                      children: /* @__PURE__ */ jsx(IconSidePanelInfo, {})
                    }),
                    /* @__PURE__ */ jsx(ExpandedFdocElements.ItemPreviewSidebarHeaderTitle, {
                      "data-similar-button": "true",
                      onClick: () => onClickToggleSidebarDesktop("assistant"),
                      tabIndex: 7,
                      children: /* @__PURE__ */ jsx(IconSidePanelAssistant, {})
                    })
                  ]
                }),
                /* @__PURE__ */ jsx(ExtraPanelSidebar.Header, {
                  children: /* @__PURE__ */ jsxs(StyledTabsList, {
                    variant: "flush",
                    children: [
                      /* @__PURE__ */ jsx(ExtraPanelSidebar.CloseButton, {
                        onClick: () => setExpandedFdocSidebarOpen(false),
                        style: {
                          marginRight: "auto"
                        }
                      }),
                      isPublished ? null : /* @__PURE__ */ jsxs(StyledTabsTrigger, {
                        variant: "apparent",
                        value: "comments",
                        disableArrowNavigation: true,
                        children: [
                          "Comments",
                          " ",
                          /* @__PURE__ */ jsx(Badge, {
                            style: {
                              marginRight: -4
                            },
                            children: comments.length
                          })
                        ]
                      }),
                      /* @__PURE__ */ jsxs(StyledTabsTrigger, {
                        variant: "apparent",
                        value: "info",
                        square: true,
                        disableArrowNavigation: true,
                        children: [
                          /* @__PURE__ */ jsx(IconSidePanelInfo, {}),
                          "Info"
                        ]
                      }),
                      /* @__PURE__ */ jsxs(StyledTabsTrigger, {
                        variant: "apparent",
                        value: "assistant",
                        disableArrowNavigation: true,
                        children: [
                          /* @__PURE__ */ jsx(IconSidePanelAssistant, {}),
                          "Ask"
                        ]
                      })
                    ]
                  })
                }),
                isPublished ? null : /* @__PURE__ */ jsx(Tabs.Content, {
                  value: "comments",
                  asChild: true,
                  children: /* @__PURE__ */ jsx(ExpandedFdocCommentsTab, {
                    comments,
                    isFullscreen,
                    onStartEditingComment: setEditingComment,
                    editingComment,
                    onCancelEditingComment,
                    resourceId: resource.id,
                    animateIn,
                    canAutoFocus
                  })
                }),
                /* @__PURE__ */ jsx(Tabs.Content, {
                  value: "assistant",
                  asChild: true,
                  children: /* @__PURE__ */ jsx(ExpandedFdocSidebarAssistant, {
                    isFullscreen,
                    canAutoFocus,
                    resourceId: resource.id
                  })
                }),
                selectedSidePanel === "info" && /* @__PURE__ */ jsx(ExtraPanelInfo, {})
              ]
            })
          })
        })
      ]
    })
  });
  return isFullscreen && isDesktopView && splitContentElement ? /* @__PURE__ */ createPortal(element, splitContentElement) : element;
});
ExpandedFdocSidebar.displayName = "ExpandedFdocSidebar";
const Container = styled(Flex).attrs({
  direction: "column",
  alignItems: "flex-end"
}).withConfig({
  displayName: "ExpandedFdocSidebar__Container",
  componentId: "sc-3e2c720d-1"
})([
  `height:100%;gap:10px;pointer-events:none;& > *{pointer-events:auto;}`
]);
const StyledTabsTrigger = styled(Tabs.Trigger).withConfig({
  displayName: "ExpandedFdocSidebar__StyledTabsTrigger",
  componentId: "sc-3e2c720d-2"
})([
  `flex-grow:0 !important;aspect-ratio:initial !important;border-radius:10px;gap:8px;padding:0 8px;height:30px;`
]);
const StyledTabsList = styled(Tabs.List).withConfig({
  displayName: "ExpandedFdocSidebar__StyledTabsList",
  componentId: "sc-3e2c720d-3"
})([
  `justify-content:flex-end;`
]);

export { ExpandedFdocSidebar as default };
//# sourceMappingURL=ExpandedFdocSidebar.js.map
